/**
 * ConsolidatedExecutivePreview
 * 
 * Preview HTML executivo do relatório consolidado.
 * Usa o MESMO FleetReportData que o PDF.
 */

import { useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from "@/components/ui/table";
import { 
  Car, 
  Users, 
  Fuel, 
  Wrench, 
  AlertTriangle, 
  TrendingUp,
  FileText,
  Download
} from "lucide-react";
import { FleetReportData } from "@/types/fleet-reports";
import {
  formatCurrency,
  formatNumber,
  formatKm,
  formatPercent,
  getKpiBadge,
  sortAlertsByPriority,
  generateExecutiveConclusion,
  generateInsights,
  KpiBadgeTone
} from "../utils/consolidated-helpers";

interface ConsolidatedExecutivePreviewProps {
  data: FleetReportData;
  onExportPdf?: () => void;
  isExporting?: boolean;
}

// =============================
// Sub-components
// =============================

function Chip({ label, tone }: { label: string; tone: KpiBadgeTone }) {
  const variants: Record<KpiBadgeTone, string> = {
    crit: "bg-destructive text-destructive-foreground",
    warn: "bg-amber-500 text-white",
    ok: "bg-emerald-600 text-white"
  };
  
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${variants[tone]}`}>
      {label}
    </span>
  );
}

function KpiCard({ 
  title, 
  value, 
  subtitle, 
  icon: Icon,
  badge 
}: { 
  title: string; 
  value: string; 
  subtitle?: string; 
  icon?: React.ComponentType<{ className?: string }>;
  badge?: { label: string; tone: KpiBadgeTone };
}) {
  return (
    <Card className="relative">
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div className="space-y-1">
            <p className="text-sm text-muted-foreground">{title}</p>
            <p className="text-2xl font-bold">{value}</p>
            {subtitle && <p className="text-xs text-muted-foreground">{subtitle}</p>}
          </div>
          <div className="flex flex-col items-end gap-2">
            {Icon && (
              <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <Icon className="h-5 w-5 text-primary" />
              </div>
            )}
            {badge && <Chip label={badge.label} tone={badge.tone} />}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function SectionTitle({ title, subtitle }: { title: string; subtitle?: string }) {
  return (
    <div className="mb-4">
      <h3 className="text-lg font-semibold">{title}</h3>
      {subtitle && <p className="text-sm text-muted-foreground">{subtitle}</p>}
    </div>
  );
}

function AlertSeverityBadge({ severity }: { severity?: string }) {
  const config: Record<string, { label: string; className: string }> = {
    critical: { label: "Crítico", className: "bg-destructive text-destructive-foreground" },
    high: { label: "Alto", className: "bg-orange-500 text-white" },
    warning: { label: "Médio", className: "bg-amber-500 text-white" },
    medium: { label: "Médio", className: "bg-amber-500 text-white" },
    low: { label: "Baixo", className: "bg-blue-500 text-white" },
    info: { label: "Info", className: "bg-blue-500 text-white" },
  };
  
  const cfg = config[severity?.toLowerCase() || ""] || { label: "Ok", className: "bg-muted text-muted-foreground" };
  
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${cfg.className}`}>
      {cfg.label}
    </span>
  );
}

// =============================
// Main Component
// =============================

export function ConsolidatedExecutivePreview({ 
  data, 
  onExportPdf,
  isExporting 
}: ConsolidatedExecutivePreviewProps) {
  const k = data.summaryKpis;
  
  // Veículos ordenados por custo
  const vehiclesSorted = useMemo(() => {
    return [...(data.byVehicle?.rows || [])].sort(
      (a, b) => (Number(b.kpis?.totalCost) || 0) - (Number(a.kpis?.totalCost) || 0)
    );
  }, [data.byVehicle?.rows]);

  // Motoristas ordenados por viagens
  const driversSorted = useMemo(() => {
    return [...(data.byDriver?.rows || [])].sort(
      (a, b) => (Number(b.kpis?.tripCount) || 0) - (Number(a.kpis?.tripCount) || 0)
    );
  }, [data.byDriver?.rows]);

  // Alertas ordenados por severidade
  const alertsSorted = useMemo(() => {
    return sortAlertsByPriority(data.alerts || []);
  }, [data.alerts]);

  // Insights (da RPC ou gerados)
  const insights = useMemo(() => generateInsights(data), [data]);

  // Conclusão executiva
  const conclusion = useMemo(() => generateExecutiveConclusion(data), [data]);

  // Badge do custo/km
  const costPerKmBadge = getKpiBadge(Number(k.costPerKm));

  // Formatação do período
  const period = data.metadata?.period;
  const periodLabel = period 
    ? `${new Date(period.from).toLocaleDateString('pt-BR')} → ${new Date(period.to).toLocaleDateString('pt-BR')}`
    : "Período não definido";

  return (
    <div className="space-y-6 pb-8">
      {/* Header */}
      <div className="bg-primary text-primary-foreground rounded-lg p-6">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold mb-1">
              Relatório Consolidado — Visão Executiva
            </h1>
            <p className="text-primary-foreground/80 text-sm">
              {data.metadata?.companyName || "Empresa"} • {periodLabel}
            </p>
            <p className="text-primary-foreground/60 text-xs mt-1">
              Gerado em {new Date(data.metadata?.generatedAt || Date.now()).toLocaleString('pt-BR')}
            </p>
          </div>
          {onExportPdf && (
            <Button 
              variant="secondary" 
              onClick={onExportPdf}
              disabled={isExporting}
              className="gap-2"
            >
              <Download className="h-4 w-4" />
              {isExporting ? "Exportando..." : "Exportar PDF"}
            </Button>
          )}
        </div>
      </div>

      {/* KPI Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <KpiCard
          title="Veículos Ativos"
          value={formatNumber(Number(k.activeVehicles || k.totalVehicles || vehiclesSorted.length))}
          subtitle={`Total: ${formatNumber(Number(k.totalVehicles || vehiclesSorted.length))}`}
          icon={Car}
        />
        <KpiCard
          title="Km no Período"
          value={formatNumber(Number(k.totalKm))}
          subtitle={`Viagens: ${formatNumber(Number(k.totalTrips || 0))}`}
          icon={TrendingUp}
        />
        <KpiCard
          title="Custo Total"
          value={formatCurrency(Number(k.totalCost))}
          subtitle={`Média/veículo: ${formatCurrency(Number(k.avgCostPerVehicle || 0))}`}
          icon={FileText}
        />
        <KpiCard
          title="Custo / Km"
          value={formatCurrency(Number(k.costPerKm))}
          subtitle="Meta: < R$ 2,50"
          badge={costPerKmBadge}
        />
        <KpiCard
          title="% Combustível"
          value={formatPercent(Number(k.fuelPercent))}
          subtitle={`Manutenção: ${formatPercent(Number(k.maintenancePercent))}`}
          icon={Fuel}
        />
        <KpiCard
          title="Riscos"
          value={`${Number(k.criticalAlerts || 0)} críticos`}
          subtitle={`${Number(k.expiredDocuments || 0)} docs vencidos`}
          icon={AlertTriangle}
          badge={Number(k.criticalAlerts) > 0 
            ? { label: "Ação imediata", tone: "crit" }
            : { label: "Estável", tone: "ok" }
          }
        />
      </div>

      {/* Insights */}
      {insights.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-semibold flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-primary" />
              Principais Insights do Período
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <ul className="space-y-2">
              {insights.slice(0, 7).map((insight, idx) => (
                <li key={idx} className="flex gap-2 text-sm text-muted-foreground">
                  <span className="text-primary font-bold">•</span>
                  <span>{insight}</span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      {/* Vehicles Ranking Table */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-semibold flex items-center gap-2">
            <Car className="h-4 w-4 text-primary" />
            Análise por Veículo — Ranking por Custo Total
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          {vehiclesSorted.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4 text-center">
              Nenhum dado de veículo disponível no período.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Veículo</TableHead>
                    <TableHead className="text-right">Km</TableHead>
                    <TableHead className="text-right">Custo</TableHead>
                    <TableHead className="text-right">R$/Km</TableHead>
                    <TableHead className="text-right">% Frota</TableHead>
                    <TableHead className="text-center">Alertas</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {vehiclesSorted.slice(0, 10).map((v, idx) => {
                    const isTop3 = idx < 3;
                    return (
                      <TableRow 
                        key={v.vehicle_id} 
                        className={isTop3 ? "bg-destructive/5" : ""}
                      >
                        <TableCell className="font-medium">
                          {isTop3 && (
                            <Badge variant="destructive" className="mr-2 text-xs">
                              #{idx + 1}
                            </Badge>
                          )}
                          {v.vehicle_label}
                        </TableCell>
                        <TableCell className="text-right">
                          {formatNumber(Number(v.kpis?.totalKm))}
                        </TableCell>
                        <TableCell className="text-right font-medium">
                          {formatCurrency(Number(v.kpis?.totalCost))}
                        </TableCell>
                        <TableCell className="text-right">
                          {Number(v.kpis?.costPerKm || 0).toFixed(2)}
                        </TableCell>
                        <TableCell className="text-right">
                          {formatPercent(Number(v.kpis?.fleetPercent))}
                        </TableCell>
                        <TableCell className="text-center">
                          {Number(v.kpis?.alertCount || 0) > 0 ? (
                            <Badge variant="destructive" className="text-xs">
                              ⚠ {v.kpis?.alertCount}
                            </Badge>
                          ) : (
                            <Badge variant="secondary" className="text-xs">OK</Badge>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
              {vehiclesSorted.length > 10 && (
                <p className="text-xs text-muted-foreground mt-2 text-center">
                  Exibindo top 10 de {vehiclesSorted.length} veículos. PDF completo contém todos.
                </p>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Drivers Section */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-semibold flex items-center gap-2">
            <Users className="h-4 w-4 text-primary" />
            Análise por Motorista — Performance Operacional
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          {driversSorted.length === 0 ? (
            <div className="bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
              <p className="text-sm text-amber-800 dark:text-amber-200">
                Não há dados suficientes de viagens/motoristas no período.
              </p>
              <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
                Verifique a integração de telemetria e o vínculo driver_id nas viagens.
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Motorista</TableHead>
                    <TableHead className="text-right">Viagens</TableHead>
                    <TableHead className="text-right">Km</TableHead>
                    <TableHead className="text-right">Custo</TableHead>
                    <TableHead className="text-right">R$/Km</TableHead>
                    <TableHead className="text-center">Ocorrências</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {driversSorted.slice(0, 10).map((d) => (
                    <TableRow key={d.driver_id}>
                      <TableCell className="font-medium">{d.driver_label}</TableCell>
                      <TableCell className="text-right">
                        {formatNumber(Number(d.kpis?.tripCount))}
                      </TableCell>
                      <TableCell className="text-right">
                        {formatNumber(Number(d.kpis?.totalKm))}
                      </TableCell>
                      <TableCell className="text-right">
                        {formatCurrency(Number(d.kpis?.totalCost))}
                      </TableCell>
                      <TableCell className="text-right">
                        {Number(d.kpis?.costPerKm || 0).toFixed(2)}
                      </TableCell>
                      <TableCell className="text-center">
                        {formatNumber(Number(d.kpis?.occurrences || d.kpis?.alertCount || 0))}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Alerts Section */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-semibold flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-primary" />
            Alertas e Riscos — Classificação por Severidade
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          {alertsSorted.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4 text-center">
              Sem alertas ativos no período.
            </p>
          ) : (
            <div className="space-y-2">
              {alertsSorted.slice(0, 12).map((alert, idx) => (
                <div 
                  key={idx}
                  className="flex items-center justify-between p-3 bg-muted/30 rounded-lg"
                >
                  <div className="flex-1">
                    <p className="text-sm font-medium">
                      {alert.title || alert.message || "-"}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Veículo: {alert.vehicle_id || "-"} • Motorista: {alert.driver_id || "-"}
                    </p>
                  </div>
                  <AlertSeverityBadge severity={alert.severity || alert.priority} />
                </div>
              ))}
              {alertsSorted.length > 12 && (
                <p className="text-xs text-muted-foreground text-center mt-2">
                  Exibindo 12 de {alertsSorted.length} alertas. PDF completo contém todos.
                </p>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Conclusion */}
      <Card className="border-primary/20">
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-semibold flex items-center gap-2">
            <FileText className="h-4 w-4 text-primary" />
            Conclusão Executiva
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0 space-y-4">
          <p className="text-sm text-muted-foreground leading-relaxed">
            {conclusion.summary}
          </p>
          
          <div>
            <h4 className="text-sm font-semibold mb-2">Recomendações</h4>
            <ul className="space-y-1">
              {conclusion.recommendations.slice(0, 8).map((rec, idx) => (
                <li key={idx} className="flex gap-2 text-sm text-muted-foreground">
                  <span className="text-primary font-bold">•</span>
                  <span>{rec}</span>
                </li>
              ))}
            </ul>
          </div>
        </CardContent>
      </Card>

      {/* Footer */}
      <div className="text-center text-xs text-muted-foreground pt-4 border-t">
        FleetONE • Preview Executivo • Gerado em {new Date().toLocaleString('pt-BR')}
      </div>
    </div>
  );
}
